import { Component, computed, effect, inject, input, model, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { I18nService } from '../i18n/i18n.service';
import {
  DEFAULT_PHONE_COUNTRY, PHONE_COUNTRIES, PhoneCountry,
  countryByIso, formatNational, fromE164, isValidNational, normaliseNational, toE164,
} from './phone';

/**
 * Country selector + national number, bound to a single **E.164** value (`+923004197674`).
 *
 * Pakistan is preselected, so the common case is: the shopper types `300 4197674` and is done.
 *
 * WHY ONE E.164 VALUE rather than exposing country + number separately: the parent screen only ever
 * wants one string to submit, and validity is then derivable from it — an empty value means "not a
 * complete number", which is exactly the condition a submit gate wants. No validity plumbing.
 *
 * The input is `type="tel"` with `inputmode="numeric"` so phones raise a keypad, but it accepts spaces
 * and a pasted `+92…` because people paste from WhatsApp; normaliseNational sorts that out.
 */
@Component({
  selector: 'app-phone-field',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="pf" [class.pf--invalid]="showRequired() || showInvalid()">
      <select class="pf__cc"
              [value]="country().iso"
              (change)="onCountry($any($event.target).value)"
              [attr.aria-label]="i18n.t('phone.country')">
        @for (c of countries; track c.iso) {
          <option [value]="c.iso">{{ c.flag }} +{{ c.dial }}</option>
        }
      </select>

      <input class="pf__num"
             type="tel"
             inputmode="numeric"
             autocomplete="tel-national"
             [id]="inputId()"
             [value]="display()"
             (input)="onNational($any($event.target).value)"
             (blur)="touched.set(true)"
             [placeholder]="country().example"
             [attr.aria-label]="i18n.t('phone.number')" />
    </div>

    @if (showRequired()) {
      <small class="pf__err">{{ requiredError() || i18n.t('phone.required') }}</small>
    } @else if (showInvalid()) {
      <small class="pf__err">{{ i18n.t('phone.invalid', { example: exampleE164() }) }}</small>
    }
  `,
  /*
   * Borders, radii and the invalid cue are copied from the checkout form's own `.field input` rules
   * rather than invented. View encapsulation stops the page's stylesheet reaching in here, so the only
   * way this field does not read as a foreign control sitting between Name and Delivery options is to
   * restate those values. Keep them in step if the form's inputs are restyled.
   *
   * The two that differ per screen are exposed as custom properties, which DO cross encapsulation:
   * a host sets `--pf-border` / `--pf-focus-ring` on `app-phone-field` to match its own inputs. The
   * defaults are checkout's, so checkout sets nothing. Plain fallbacks, never a token pointing at
   * another token — that resolves at the declaring element and freezes.
   */
  styles: [`
    :host { display: block; }

    .pf { display: flex; gap: 0.4rem; align-items: stretch; }
    .pf__cc {
      flex: 0 0 auto;
      /* Fits the flag plus a 3-digit dial code without truncating at 360px. Windows renders a
         regional-indicator pair as the letters ("PK"), which is still the right information. */
      min-width: 5.4rem;
      padding: 0.7rem 0.4rem;
      border: 1px solid var(--pf-border, #d7deea); border-radius: 10px;
      background: #fff; font: inherit; cursor: pointer;
      /* The code is Latin digits; never let it mirror inside an Urdu page. */
      direction: ltr; unicode-bidi: isolate;
    }
    .pf__num {
      flex: 1 1 auto; min-width: 0;
      padding: 0.7rem 0.9rem;
      border: 1px solid var(--pf-border, #d7deea); border-radius: 10px;
      font: inherit;
      /* A phone number is always read left-to-right, whatever the page direction. */
      direction: ltr; unicode-bidi: isolate; text-align: start;
    }
    .pf__cc:focus, .pf__num:focus {
      outline: none;
      border-color: var(--color-primary, #27afb6);
      box-shadow: 0 0 0 3px var(--pf-focus-ring, rgba(39, 175, 182, 0.15));
    }
    .pf--invalid .pf__cc,
    .pf--invalid .pf__num { border-color: #f0a8b4; background: #fff7f8; }
    .pf--invalid .pf__cc:focus,
    .pf--invalid .pf__num:focus {
      border-color: #e11d48; box-shadow: 0 0 0 3px rgba(225, 29, 72, 0.15);
    }
    .pf__err { display: block; margin-top: 0.3rem; color: #e11d48; font-size: 0.72rem; }
  `],
})
export class PhoneFieldComponent {
  /** The bound value, always E.164 or '' — '' meaning "not a complete number yet". */
  readonly value = model<string>('');
  /** Show the error before the field has been touched (e.g. after a failed submit). */
  readonly forceValidate = input(false);
  readonly inputId = input('phone');
  /** Lets a screen keep its own "required" wording; falls back to this component's. */
  readonly requiredError = input('');
  readonly required = input(true);

  protected readonly i18n = inject(I18nService);
  protected readonly countries = PHONE_COUNTRIES;

  protected readonly country = signal<PhoneCountry>(DEFAULT_PHONE_COUNTRY);
  /** Raw digits the shopper has entered, unformatted. */
  protected readonly national = signal('');
  protected readonly touched = signal(false);

  protected readonly display = computed(() => formatNational(this.national(), this.country()));

  /**
   * The example number for the error message, wrapped in U+2066 LEFT-TO-RIGHT ISOLATE … U+2069 POP.
   *
   * Without it the Urdu message renders the groups backwards — "4197674 300 92+" — because bidi
   * resolves "+92", "300" and "4197674" as separate runs inside an RTL paragraph and lays them out
   * right-to-left. The isolate keeps them one left-to-right unit. Characters rather than a <span dir>
   * so the sentence stays a SINGLE translation key: Urdu puts the example in a different position, and
   * splitting the string into fragments around markup is precisely what breaks that.
   */
  protected readonly exampleE164 = computed(() =>
    `\u2066+${this.country().dial} ${this.country().example}\u2069`);

  /**
   * Nothing entered at all. Shown immediately rather than on blur, which is what this page already does
   * for its other required fields — a shopper should see what the form wants before they hunt for it.
   */
  protected readonly showRequired = computed(() => this.required() && this.national().length === 0);

  /**
   * Something has been typed, but it is not a complete number yet.
   *
   * Public because a screen where the phone is OPTIONAL cannot otherwise tell "left blank" (fine)
   * from "half-typed" (must not be silently discarded): {@link value} publishes '' for both. Such a
   * screen reads this through a viewChild on submit. Screens with a required phone need only the
   * empty check.
   */
  readonly partial = computed(() =>
    this.national().length > 0 && !isValidNational(this.national(), this.country()));

  /**
   * Something entered, but not a complete number. Held back until blur (or a failed submit), because
   * "that isn't valid" while someone is typing digit 3 of 10 is just nagging.
   */
  protected readonly showInvalid = computed(() =>
    (this.touched() || this.forceValidate()) && this.partial());

  constructor() {
    // Adopt a value set from OUTSIDE (a saved address, the signed-in profile) — but never fight the
    // shopper mid-type: only re-parse when the incoming value differs from what this field produced.
    effect(() => {
      const incoming = this.value();
      if (incoming === toE164(this.national(), this.country())) return;

      const parsed = fromE164(incoming);
      this.country.set(parsed.country);
      this.national.set(parsed.national);

      // Write the canonical form straight back. Numbers arrive from the profile and the address book in
      // whatever shape they were once saved ("0300 419 7674"), and without this they would reach the
      // API unnormalised AND slip past the parent's gate, which only asks whether the value is empty —
      // i.e. a stored bad number would be the one case validation never covered.
      // This re-runs the effect once; the guard above then matches and it stops.
      this.emit();
    });
  }

  protected onCountry(iso: string): void {
    const c = countryByIso(iso);
    this.country.set(c);
    // Re-normalise against the NEW country: a trunk zero or an old country code that was valid for the
    // previous one may not be for this one.
    this.national.set(normaliseNational(this.national(), c));
    this.emit();
  }

  protected onNational(raw: string): void {
    this.national.set(normaliseNational(raw, this.country()));
    this.emit();
  }

  /** Publishes '' whenever the number is incomplete, so the parent's gate is simply "is it empty?". */
  private emit(): void {
    this.value.set(toE164(this.national(), this.country()));
  }
}
